import React, { useState, type RefObject } from "react"

import { getStatusLabel, normalizeRunStatus } from "@/runs/status"
import {
  Button,
  TableColumnToggle,
  Tabs,
  TabsList,
  TabsTrigger,
  type ColumnDef,
  type SortDescriptor,
} from "@coval/ui"
import { FolderOpen, Layers, Trash2 } from "lucide-react"

import {
  ActiveFilterBar,
  AddFilterButton,
} from "@/components/filters/ActiveFilterBar"
import { QuerySearchInput } from "@/components/filters/QuerySearchInput"
import { DeleteRunsModal } from "@/components/modals/DeleteRunsModal"
import { AddToHumanReviewProjectDropdown } from "@/components/table-actions/AddToHumanReviewProjectDropdown"
import { ReportButton } from "@/components/table-actions/ReportButton"
import { TableCsvDownloadButton } from "@/components/table-actions/TableCsvDownloadButton"
import { BrowseTable as Table } from "@/components/tables/BrowseTable"
import { TableDisplaySlot } from "@/components/tables/TableDisplayScope"
import type { RunsTableResult } from "@/types/tables/RunsTable"
import type { SimulationsTableResult } from "@/types/tables/SimulationsTable"

import { ReportRunLimitDialog } from "../../multi-run"
import type { FilterSection } from "../types/filters"
import type { RunsSavedViewsTransportState } from "../types/savedViews"
import { RUNS_VIEW_MODE, type RunsViewMode } from "../types/viewMode"
import { RunsBulkTagDropdown } from "./RunsBulkTagDropdown"
import { RunsFilterChips } from "./RunsFilterChips"
import { RunsFilterDropdown } from "./RunsFilterDropdown"
import { RunsSavedViewsDropdown } from "./RunsSavedViewsDropdown"
import { RunsTableEmptyState, RunsTableQueryError } from "./RunsTableEmptyState"

const RUNS_TABLE_CLASS_NAME =
  "h-full [&_[role=row][aria-hidden=true]>div:first-child]:self-stretch"

type HumanReviewProjectDropdownProps = React.ComponentProps<
  typeof AddToHumanReviewProjectDropdown
>

interface RunsTableControlsProps extends Pick<
  HumanReviewProjectDropdownProps,
  | "fetchHumanReviewProjectWithSimulationIds"
  | "fetchSimulationOutputIdsForRun"
  | "humanReviewProjects"
  | "isProjectsLoading"
  | "isProjectsError"
  | "onRetryProjects"
  | "sendProjectUpdate"
  | "updateHumanReviewProject"
> {
  clearAllFilters: () => void
  clearSelectedKeys: () => void
  handleBulkDelete: () => void
  handleCompareRuns: () => void
  handleViewModeChange: (viewMode: RunsViewMode) => void
  hasActiveFilters: boolean
  isLoading: boolean
  isShowingPlaceholderRows: boolean
  onClear: () => void
  onSearchChange: (value: string) => void
  savedViewsTransport?: RunsSavedViewsTransportState
  searchInputRef: RefObject<HTMLInputElement | null>
  searchValue: string
  searchResetKey?: number
  selectedAgentId: string | null
  selectedCount: number
  selectedKeySet: Set<string>
  filterQueriesEnabled: boolean
  tableRuns: RunsTableResult[]
  tableSimulations: SimulationsTableResult[]
  viewMode: RunsViewMode
}

export function RunsTableControls({
  clearAllFilters,
  clearSelectedKeys,
  fetchHumanReviewProjectWithSimulationIds,
  fetchSimulationOutputIdsForRun,
  handleBulkDelete,
  handleCompareRuns,
  handleViewModeChange,
  hasActiveFilters,
  humanReviewProjects,
  isProjectsLoading,
  isProjectsError,
  onRetryProjects,
  isLoading,
  isShowingPlaceholderRows,
  onClear,
  onSearchChange,
  savedViewsTransport,
  searchInputRef,
  searchValue,
  searchResetKey,
  selectedAgentId,
  selectedCount,
  selectedKeySet,
  sendProjectUpdate,
  filterQueriesEnabled,
  tableRuns,
  tableSimulations,
  updateHumanReviewProject,
  viewMode,
}: RunsTableControlsProps) {
  const [filterOpen, setFilterOpen] = useState(false)
  const [filterSection, setFilterSection] = useState<FilterSection>(null)
  return (
    <div className="relative z-10 table-pad flex-none border-b border-coval-border space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div
          className={
            selectedCount > 0
              ? "hidden"
              : "flex w-64 max-w-full items-center gap-2"
          }
        >
          <QuerySearchInput
            inputRef={searchInputRef}
            disabled={selectedCount > 0}
            searchLabel="Search runs"
            resetKey={searchResetKey}
            placeholder="Search..."
            className="min-w-0 flex-1"
            onClear={onClear}
            value={searchValue}
            onValueChange={onSearchChange}
            aria-label="Search runs"
            data-testid="runs-search-input"
          />
        </div>
        {selectedCount > 0 ? (
          <>
            <ReportButton
              onClick={handleCompareRuns}
              disabled={isLoading || isShowingPlaceholderRows}
              aria-label={`Generate report from ${selectedCount} selected ${
                viewMode === RUNS_VIEW_MODE.RUNS ? "runs" : "simulations"
              }`}
            />
            <AddToHumanReviewProjectDropdown
              itemType={viewMode === RUNS_VIEW_MODE.RUNS ? "run" : "simulation"}
              selectedKeys={selectedKeySet}
              currentData={
                viewMode === RUNS_VIEW_MODE.RUNS ? tableRuns : tableSimulations
              }
              humanReviewProjects={humanReviewProjects}
              isProjectsLoading={isProjectsLoading}
              isProjectsError={isProjectsError}
              onRetryProjects={onRetryProjects}
              fetchHumanReviewProjectWithSimulationIds={
                fetchHumanReviewProjectWithSimulationIds
              }
              updateHumanReviewProject={updateHumanReviewProject}
              sendProjectUpdate={sendProjectUpdate}
              fetchSimulationOutputIdsForRun={fetchSimulationOutputIdsForRun}
              isDisabled={isLoading || isShowingPlaceholderRows}
              onSuccess={clearSelectedKeys}
            />
            {viewMode === RUNS_VIEW_MODE.RUNS && (
              <RunsBulkTagDropdown
                selectedKeys={selectedKeySet}
                currentData={tableRuns}
                isDisabled={isLoading || isShowingPlaceholderRows}
              />
            )}
            <div className="flex shrink-0 items-center gap-2">
              {viewMode === RUNS_VIEW_MODE.RUNS && (
                <Button
                  variant="danger"
                  onClick={handleBulkDelete}
                  disabled={isLoading || isShowingPlaceholderRows}
                  aria-label={`Delete ${selectedCount} selected items`}
                >
                  <Trash2 className="size-4" />
                  Delete
                </Button>
              )}
              <span className="text-sm text-coval-text-muted">
                {selectedCount} selected
              </span>
            </div>
          </>
        ) : (
          <>
            <Tabs
              data-testid="runs-view-toggle"
              value={viewMode}
              onValueChange={(value) =>
                handleViewModeChange(value as RunsViewMode)
              }
            >
              <TabsList variant="pill">
                <TabsTrigger
                  data-testid="runs-view-toggle-runs"
                  variant="pill"
                  value={RUNS_VIEW_MODE.RUNS}
                >
                  <FolderOpen className="w-4 h-4" />
                  Runs
                </TabsTrigger>
                <TabsTrigger
                  data-testid="runs-view-toggle-simulations"
                  variant="pill"
                  value={RUNS_VIEW_MODE.SIMULATIONS}
                >
                  <Layers className="w-4 h-4" />
                  Simulated
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <RunsFilterDropdown
              open={filterOpen}
              onOpenChange={setFilterOpen}
              section={filterSection}
              onSectionChange={setFilterSection}
              selectedAgentId={selectedAgentId}
              filterEnabled={filterQueriesEnabled}
              viewMode={viewMode}
            />

            <TableDisplaySlot />
            {savedViewsTransport ? (
              <RunsSavedViewsDropdown
                savedViewsTransport={savedViewsTransport}
              />
            ) : null}
          </>
        )}
      </div>
      {selectedCount === 0 && hasActiveFilters && (
        <ActiveFilterBar
          onClear={clearAllFilters}
          addControl={
            <RunsFilterDropdown
              selectedAgentId={selectedAgentId}
              filterEnabled={filterQueriesEnabled}
              trigger={<AddFilterButton />}
            />
          }
        >
          <RunsFilterChips
            filterEnabled={filterQueriesEnabled}
            onEditFilter={(section) => {
              setFilterSection(section)
              setFilterOpen(true)
            }}
          />
        </ActiveFilterBar>
      )}
    </div>
  )
}

interface RunsTableDataViewProps {
  isError?: boolean
  isFetching?: boolean
  onRetry?: () => void
  hideControls: boolean
  runToggleColumns: ColumnDef<RunsTableResult>[]
  simToggleColumns: ColumnDef<SimulationsTableResult>[]
  activeRowId: string | null
  columnVisibility: Record<string, boolean>
  handleColumnVisibilityChange: (visibility: Record<string, boolean>) => void
  fetchNextPage: () => Promise<unknown>
  handlePointerEnter: (rowId: string) => void
  handlePointerLeave: (rowId: string) => void
  handleDownloadCsv: () => void
  handleRowClick: (runId: string) => void
  handleScrollPositionChange: (offset: number) => void
  /** The run's own page, for new-tab opens and the row menu's Copy URL. */
  runHref: (runId: string) => string
  /**
   * The simulation result's own page, for new-tab opens and the row menu's
   * Copy URL.
   */
  simulationHref: (runId: string, simulationOutputId: string) => string
  handleSimulationRowClick: (runId: string, simulationOutputId: string) => void
  handleSortChange: (sort: SortDescriptor | null) => void
  /** Each column's header in the downloaded CSV, keyed by column id; the download dialog lists these. */
  csvHeaderById: Record<string, string>
  hasActiveFilters: boolean
  initialScrollOffset: number
  isFetchingNextPage: boolean
  isLoading: boolean
  /** Replaces the default "New Simulation" empty state, for a caller whose rows are not user-launched. */
  emptyState?: React.ReactNode
  resetLabel?: "Clear filters" | "Clear search"
  onResetFilters?: () => void
  onSimulate: () => void
  runColumns: ColumnDef<RunsTableResult>[]
  selectedKeySet: Set<string>
  setSelectedKeys: (ids: Set<string>) => void
  simColumns: ColumnDef<SimulationsTableResult>[]
  sortDefault: SortDescriptor
  tableHasNextPage: boolean
  tableRuns: RunsTableResult[]
  tableSimulations: SimulationsTableResult[]
  searchValue: string
  viewMode: RunsViewMode
}

export function RunsTableDataView({
  isError = false,
  isFetching = false,
  onRetry,
  hideControls,
  runToggleColumns,
  simToggleColumns,
  activeRowId,
  columnVisibility,
  handleColumnVisibilityChange,
  fetchNextPage,
  handlePointerEnter,
  handlePointerLeave,
  handleDownloadCsv,
  handleRowClick,
  handleScrollPositionChange,
  handleSimulationRowClick,
  runHref,
  simulationHref,
  handleSortChange,
  csvHeaderById,
  hasActiveFilters,
  initialScrollOffset,
  isFetchingNextPage,
  isLoading,
  emptyState,
  resetLabel,
  onResetFilters,
  onSimulate,
  runColumns,
  searchValue,
  selectedKeySet,
  setSelectedKeys,
  simColumns,
  sortDefault,
  tableHasNextPage,
  tableRuns,
  tableSimulations,
  viewMode,
}: RunsTableDataViewProps) {
  const isRunsMode = viewMode === RUNS_VIEW_MODE.RUNS
  const hasRows = isRunsMode
    ? tableRuns.length > 0
    : tableSimulations.length > 0
  const queryError =
    isError && onRetry ? (
      <RunsTableQueryError isFetching={isFetching} onRetry={onRetry} />
    ) : null
  if (isError && !hasRows) {
    return queryError
  }
  return (
    <div className="runs-table flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden">
      {queryError}
      <div
        data-testid="runs-list"
        className="flex-1 min-h-0 min-w-0 max-w-full"
        data-covi-view-list={isRunsMode ? "run" : "simulation"}
        data-covi-list-showing={`${
          isRunsMode ? tableRuns.length : tableSimulations.length
        } loaded${tableHasNextPage ? " (more available)" : ""}`}
        data-covi-list-search={searchValue || undefined}
        data-covi-list-filters={hasActiveFilters ? "active" : undefined}
      >
        {isRunsMode ? (
          <Table
            displayControls={!hideControls}
            groupOptions={[
              {
                id: "status",
                label: "Status",
                value: (row) => normalizeRunStatus(row.status),
                format: getStatusLabel,
              },
              {
                id: "simulator",
                label: "Simulator",
                value: (row) => row.model_type,
                format: (value) =>
                  value
                    .replace("MODEL_TYPE_", "")
                    .replaceAll("_", " ")
                    .toLowerCase(),
              },
            ]}
            key={RUNS_VIEW_MODE.RUNS}
            columnControls={
              <TableColumnToggle
                inline
                label="Visible columns"
                columns={runToggleColumns}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                searchable
              />
            }
            aria-label="Runs table"
            data={tableRuns}
            columns={runColumns}
            getRowId={(run) => run.id}
            csvControl={
              <TableCsvDownloadButton
                onClick={handleDownloadCsv}
                rowCount={tableRuns.length}
                columns={runColumns
                  .filter((column) => columnVisibility[column.id] ?? true)
                  .map(
                    (column) =>
                      csvHeaderById[column.id] ??
                      column.headerLabel ??
                      column.id,
                  )}
                disabled={isLoading || tableRuns.length === 0}
              />
            }
            rowCopyId
            rowHref={(run) => runHref(run.id)}
            rowAttributes={(run) => ({
              "data-covi-highlight-id": run.id,
              "data-covi-name": run.display_name ?? undefined,
              "data-covi-status": run.status,
            })}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            defaultSort={sortDefault}
            // The URL owns this sort; a tab-stored sort would override a shared link.
            sortStorageKey={null}
            onSortChange={handleSortChange}
            onRowClick={(run) => handleRowClick(run.id)}
            activeRowId={activeRowId}
            onRowPointerEnter={handlePointerEnter}
            onRowPointerLeave={handlePointerLeave}
            onLoadMore={() => {
              void fetchNextPage()
            }}
            hasMore={tableHasNextPage}
            isFetchingMore={isFetchingNextPage}
            isLoading={isLoading}
            initialScrollOffset={initialScrollOffset}
            onScrollChange={handleScrollPositionChange}
            emptyState={
              emptyState ?? (
                <RunsTableEmptyState
                  isRunsMode
                  onSimulate={onSimulate}
                  hasActiveFilters={
                    hasActiveFilters || Boolean(searchValue.trim())
                  }
                  onResetFilters={onResetFilters}
                  resetLabel={resetLabel}
                />
              )
            }
            className={RUNS_TABLE_CLASS_NAME}
            selectable
            selectedIds={selectedKeySet}
            onSelectionChange={setSelectedKeys}
          />
        ) : (
          <Table
            displayControls={!hideControls}
            groupOptions={[
              {
                id: "status",
                label: "Status",
                value: (row) => normalizeRunStatus(row.status),
                format: getStatusLabel,
              },
              {
                id: "simulator",
                label: "Simulator",
                value: (row) => row.model_type,
                format: (value) =>
                  value
                    .replace("MODEL_TYPE_", "")
                    .replaceAll("_", " ")
                    .toLowerCase(),
              },
            ]}
            key={RUNS_VIEW_MODE.SIMULATIONS}
            columnControls={
              <TableColumnToggle
                inline
                label="Visible columns"
                columns={simToggleColumns}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={handleColumnVisibilityChange}
                searchable
              />
            }
            aria-label="Simulated conversations table"
            data={tableSimulations}
            columns={simColumns}
            getRowId={(simulation) => simulation.simulation_output_id}
            csvControl={
              <TableCsvDownloadButton
                onClick={handleDownloadCsv}
                rowCount={tableSimulations.length}
                columns={simColumns
                  .filter((column) => columnVisibility[column.id] ?? true)
                  .map(
                    (column) =>
                      csvHeaderById[column.id] ??
                      column.headerLabel ??
                      column.id,
                  )}
                disabled={isLoading || tableSimulations.length === 0}
              />
            }
            rowCopyId
            rowHref={(simulation) =>
              simulationHref(simulation.run_id, simulation.simulation_output_id)
            }
            rowAttributes={(simulation) => ({
              "data-covi-highlight-id": simulation.simulation_output_id,
              "data-covi-status": simulation.status,
            })}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            defaultSort={sortDefault}
            // The URL owns this sort; a tab-stored sort would override a shared link.
            sortStorageKey={null}
            onSortChange={handleSortChange}
            onRowClick={(simulation) =>
              handleSimulationRowClick(
                simulation.run_id,
                simulation.simulation_output_id,
              )
            }
            activeRowId={activeRowId}
            onRowPointerEnter={handlePointerEnter}
            onRowPointerLeave={handlePointerLeave}
            onLoadMore={() => {
              void fetchNextPage()
            }}
            hasMore={tableHasNextPage}
            isFetchingMore={isFetchingNextPage}
            isLoading={isLoading}
            initialScrollOffset={initialScrollOffset}
            onScrollChange={handleScrollPositionChange}
            emptyState={
              emptyState ?? (
                <RunsTableEmptyState
                  isRunsMode={false}
                  hasActiveFilters={
                    hasActiveFilters || Boolean(searchValue.trim())
                  }
                  onResetFilters={onResetFilters}
                  resetLabel={resetLabel}
                  onSimulate={onSimulate}
                />
              )
            }
            className={RUNS_TABLE_CLASS_NAME}
            selectable
            selectedIds={selectedKeySet}
            onSelectionChange={setSelectedKeys}
          />
        )}
      </div>
    </div>
  )
}

interface RunsTableDialogsProps {
  failedRunIds: string[]
  handleConfirmDelete: () => void
  handleDeleteModalClose: () => void
  handleReportLimitConfirm: () => void
  isBulkDeleting: boolean
  isDeleteModalOpen: boolean
  isReportLimitOpen: boolean
  onReportLimitClose: () => void
  reportLimitItemLabel: "runs" | "simulations"
  runsToDeleteCount: number
}

export function RunsTableDialogs({
  failedRunIds,
  handleConfirmDelete,
  handleDeleteModalClose,
  handleReportLimitConfirm,
  isBulkDeleting,
  isDeleteModalOpen,
  isReportLimitOpen,
  onReportLimitClose,
  reportLimitItemLabel,
  runsToDeleteCount,
}: RunsTableDialogsProps) {
  return (
    <>
      <DeleteRunsModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleConfirmDelete}
        itemCount={runsToDeleteCount}
        isDeleting={isBulkDeleting}
        failedRunIds={failedRunIds}
      />
      <ReportRunLimitDialog
        itemLabel={reportLimitItemLabel}
        onCancel={onReportLimitClose}
        onContinue={handleReportLimitConfirm}
        open={isReportLimitOpen}
      />
    </>
  )
}
